// react
import { ChangeEvent, ReactNode, useContext, useState } from 'react';

// types
import { DocumentType, Entity, ExpenseType } from '../../../../../@types';
import { entityTypesDefault } from '../../../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../../../hooks/useFetch';

// context
import { ContextUser, ContextUserEntity } from '../../../../../App.context';
import { ContextProject, ContextUserProjectPermissions } from '../../Project.context';

// mui
import {
	Alert,
	Box,
	Container,
	Grid,
	Paper,
	SelectChangeEvent,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';

// pages
import { CardDocType } from './CardDocType/CardDocType';
import { CardExpenseType } from './CardExpenseType/CardExpenseType';

// forms
import { FormAddDocType } from '../../../../forms/FormAddDocType/FormAddDocType';
import { FormAddExpenseType } from '../../../../forms/FormAddExpenseType/FormAddExpenseType';
import { FormSearch } from '../../../../forms/FormSearch/FormSearch';

// fields
import { Checkbox } from '../../../../fields/Checkbox/Checkbox';
import { Select } from '../../../../fields/Select/Select';

// dialogs
import { DialogUpdateExpenseTypes } from '../../../../dialogs/DialogUpdateExpenseTypes/DialogUpdateExpenseTypes';

// ui
import { Empty } from '../../../../ui/Empty/Empty';
import { TableHeaders } from '../../../../ui/TableHeaders/TableHeaders';

// theme
import { theme } from '../../../../../theme';

export const ExpenseTypes = () => {
	// hooks
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [activeExpenseType, setActiveExpenseType] = useState<ExpenseType | null>(null);
	const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	const [values, setValues] = useState<{
		docTypeView: 'ALL' | 'SELECTED';
		entity: string;
		hasDefault: boolean;
	}>({
		docTypeView: 'ALL',
		entity: '',
		hasDefault: true,
	});

	// params
	const paramDialog = searchParams.get('dialog');
	const paramQ = searchParams.get('q');

	// vars
	const hasEntitySelect = !userEntity;

	let activeDocTypes = (activeExpenseType?.documentTypes ?? []) as DocumentType[];
	let urlDocumentTypes = `${process.env.REACT_APP_API_URL}/document-types?populate=entity&order=ASC&orderBy=name`;
	let urlExpenseTypes = `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&populate=documentTypes&order=ASC&orderBy=name&populate=entity`;

	if (userEntity) {
		// show default and entity expense types and doc types
		urlDocumentTypes += `&entity=null,${userEntity.id}`;
		urlExpenseTypes += `&entity=null,${userEntity.id}`;
	} else if (values.entity) {
		// show default and entity doc types
		urlDocumentTypes += `&entity=null,${values.entity}`;
		// show only entity expense types
		urlExpenseTypes += `&entity=${values.entity}`;
	} else {
		// show only default doc types and expense types
		urlDocumentTypes += `&entity=null`;
		urlExpenseTypes += `&entity=null`;
	}

	// fetch doc types
	const { isLoading: isLoadingDocumentTypes, refetch: refetchDocumentTypes } = useFetch({
		isEnabled: Boolean(user),
		url: urlDocumentTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocTypes(res.data);
			}
		},
	});

	// fetch expense types
	const { isLoading: isLoadingExpenseTypes, refetch: refetchExpenseTypes } = useFetch({
		isEnabled: Boolean(project?.id && user),
		url: urlExpenseTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseTypes(res.data);
				setActiveExpenseType(res.data[0]);
			}
		},
	});

	// fetch entities
	useFetch({
		isEnabled: Boolean(user),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&type=${entityTypesDefault.join(',')}&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// handlers
	const handleChangeDocType = (e: ChangeEvent<HTMLInputElement>, docType: DocumentType) => {
		if (e.target.checked) {
			// add doc type
			activeDocTypes.push(docType);
		} else {
			// remove doc type
			activeDocTypes = activeDocTypes.filter((d) => d.id !== docType.id);
		}

		// clone new expense types
		const expenseTypesCloned = structuredClone(expenseTypes);

		// define active expense type from clone
		const expenseTypeToBeUpdated = expenseTypesCloned.find(
			(exp) => exp.id === activeExpenseType?.id
		);

		if (expenseTypeToBeUpdated) {
			// format and set doc types
			expenseTypeToBeUpdated.documentTypes = activeDocTypes.map((dt) => {
				return {
					...dt,
					isPrivate: 'isPrivate' in dt ? dt.isPrivate : false,
					isRequired: 'isRequired' in dt ? dt.isRequired : true,
				};
			});

			setActiveExpenseType(expenseTypeToBeUpdated);
			setExpenseTypes(expenseTypesCloned);
		}
	};

	const handleChangeSwitch = (
		e: ChangeEvent<HTMLInputElement>,
		docType: DocumentType,
		prop: 'isPrivate' | 'isRequired'
	) => {
		// clone new expense types
		const expenseTypesCloned = structuredClone(expenseTypes);

		// find active expense type
		const expenseTypeToBeUpdated = expenseTypesCloned.find(
			(exp) => exp.id === activeExpenseType?.id
		);

		if (expenseTypeToBeUpdated) {
			// find selected doc type
			const docTypeFound = (expenseTypeToBeUpdated?.documentTypes as DocumentType[]).find(
				(d) => d.id === docType.id
			);

			// update property
			if (docTypeFound) {
				docTypeFound[prop] = e.target.checked;
			}

			setActiveExpenseType(expenseTypeToBeUpdated);
			setExpenseTypes(expenseTypesCloned);
		}
	};

	const handleChangeEntity = (e: SelectChangeEvent<unknown>, child: ReactNode) => {
		setValues((valuesPrev) => {
			return {
				...valuesPrev,
				entity: e.target.value as string,
			};
		});
	};

	// filter doc types by search query and view
	const activeDocTypeIds = new Set(activeDocTypes?.map((d) => d.id) ?? []);
	const docTypesFiltered = docTypes.filter((docType) => {
		const matchesSearch = !paramQ || docType.name.toLowerCase().includes(paramQ.toLowerCase());
		const matchesView = values.docTypeView === 'ALL' || activeDocTypeIds.has(docType.id);
		return matchesSearch && matchesView;
	});

	// filter expense types
	const expenseTypesFiltered = expenseTypes.filter(
		(ex) => values.hasDefault || (!values.hasDefault && ex.entity)
	);

	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: entity.id,
		};
	});

	return (
		<>
			<Container maxWidth={false}>
				<Grid container spacing={2}>
					<Grid item xs={12} lg={4}>
						<Grid
							container
							direction="column"
							spacing={2}
							sx={(theme) => {
								return {
									height: `calc(100% + ${theme.spacing(2)})`,
								};
							}}>
							<Grid item sx={{ width: '100%' }}>
								<Paper
									sx={(theme) => {
										return {
											position: 'relative',
											padding: theme.spacing(2),
										};
									}}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Typography variant="h3">{t('expenseTypes')}</Typography>
										</Grid>
										{userProjectPermissions?.expenseType?.create && (
											<Grid item xs={12}>
												<FormAddExpenseType
													onSuccess={() => {
														refetchExpenseTypes();
													}}
												/>
											</Grid>
										)}
									</Grid>
								</Paper>
							</Grid>
							<Grid item flex={1} sx={{ width: '100%' }}>
								<Paper sx={{ padding: 2, height: '100%' }}>
									<Box
										sx={{
											position: 'relative',
											height: {
												lg: '100%',
											},
										}}>
										<Grid
											container
											direction="column"
											spacing={2}
											sx={{ height: `calc(100% + ${theme.spacing(4)})` }}>
											{hasEntitySelect && (
												<Grid item sx={{ width: '100%' }}>
													<Select
														label={t('entity')}
														onChange={handleChangeEntity}
														options={optionsEntities}
														value={values.entity}
													/>
												</Grid>
											)}
											{!hasEntitySelect && (
												<Grid item>
													<Checkbox
														checked={values.hasDefault}
														id="expense-types-filter-default"
														label={t('showDefault')}
														name="hasDefault"
														onChange={(e) => {
															// set first expense type with entity as active
															const activeExpenseTypeNew = expenseTypes.find((ex) => ex.entity);

															if (activeExpenseTypeNew) {
																setActiveExpenseType(activeExpenseTypeNew);
															}

															// set values
															setValues((valuesPrev) => {
																return {
																	...valuesPrev,
																	hasDefault: e.target.checked,
																};
															});
														}}
													/>
												</Grid>
											)}
											<Grid
												flex={1}
												item
												sx={{ position: 'relative', maxWidth: '100% !important' }}>
												{expenseTypesFiltered.length ? (
													<Box
														sx={(theme) => {
															return {
																overflowY: 'auto',
																maxHeight: {
																	lg: 610,
																},
															};
														}}>
														<Grid container spacing={2}>
															{expenseTypesFiltered.map((expenseType, i) => {
																return (
																	<Grid item xs={12}>
																		<CardExpenseType
																			expenseType={expenseType}
																			isActive={expenseType.id === activeExpenseType?.id}
																			key={`${expenseType.id}-${i}`}
																			onClick={() => {
																				setActiveExpenseType(expenseType);
																			}}
																			onSuccessDelete={() => {
																				refetchExpenseTypes();
																			}}
																			onSuccessEdit={() => {
																				refetchExpenseTypes();
																			}}
																			onSuccessUpdateStatus={() => {
																				refetchExpenseTypes();
																			}}
																		/>
																	</Grid>
																);
															})}
														</Grid>
													</Box>
												) : (
													<Empty
														content={{ children: t('empty.expenseTypes.content') }}
														IconCircleProps={{ color: 'grey', type: 'expenseType' }}
														isLoading={isLoadingExpenseTypes}
														sx={(theme) => {
															return {
																padding: theme.spacing(2),
																pointerEvents: 'none',
																position: {
																	lg: 'absolute',
																},
																width: '100%',
																height: '100%',
															};
														}}
														title={{ children: t('empty.expenseTypes.title') }}
													/>
												)}
											</Grid>
										</Grid>
									</Box>
								</Paper>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} lg={8}>
						<Grid container direction="column" spacing={2}>
							<Grid item>
								<Paper
									sx={(theme) => {
										return {
											padding: theme.spacing(2),
										};
									}}>
									<Grid
										container
										direction="column"
										spacing={2}
										sx={(theme) => {
											return {
												height: `calc(100% + ${theme.spacing(2)})`,
											};
										}}>
										<Grid item>
											<Typography variant="h3">{t('documentTypes')}</Typography>
										</Grid>
										<Grid item>
											<FormAddDocType
												onSuccess={() => {
													refetchDocumentTypes();
												}}
											/>
										</Grid>
									</Grid>
								</Paper>
							</Grid>
							<Grid item>
								<Paper
									sx={{
										position: 'relative',
										padding: 2,
										minHeight: {
											lg: 600,
										},
									}}>
									{docTypes.length ? (
										<Grid container spacing={2}>
											<Grid item xs={12}>
												<Grid alignItems="flex-end" container spacing={2}>
													<Grid item xs={12} md={true}>
														<FormSearch
															InputProps={{
																label: hasEntitySelect ? t('search') : undefined,
																placeholder: t('searchByName'),
															}}
														/>
													</Grid>
													<Grid item xs={12} md="auto">
														<ToggleButtonGroup
															color="primary"
															exclusive={true}
															fullWidth={true}
															onChange={(_, value) => {
																if (value) {
																	setValues((v) => ({ ...v, docTypeView: value }));
																}
															}}
															value={values.docTypeView}>
															<ToggleButton value="ALL">{t('all')}</ToggleButton>
															<ToggleButton value="SELECTED">{t('selected')}</ToggleButton>
														</ToggleButtonGroup>
													</Grid>
												</Grid>
											</Grid>
											<Grid item xs={12}>
												<Grid container spacing={2}>
													<Grid item xs={12}>
														<TableHeaders
															hasBorder={true}
															hasShadow={false}
															headers={[
																{
																	GridProps: {
																		xs: 6,
																	},
																	label: t('name'),
																	value: 'name',
																},
																{
																	GridProps: {
																		xs: 2.5,
																	},
																	label: t('mandatory'),
																	tooltip: {
																		title: t('tooltip.mandatory'),
																	},
																	value: 'isRequired',
																},
																{
																	GridProps: {
																		xs: 2.5,
																	},
																	label: t('private'),
																	tooltip: {
																		title: t('tooltip.private'),
																	},
																	value: 'isPrivate',
																},
															]}
															size="sm"
															spacing={2}
														/>
													</Grid>
													<Grid item xs={12}>
														<Box
															sx={(theme) => {
																return {
																	overflowY: 'auto',
																	maxHeight: {
																		lg: 610,
																	},
																};
															}}>
															<Grid container spacing={2}>
																{docTypesFiltered.length > 0 ? (
																	docTypesFiltered.map((docType, i) => {
																		const docTypeActive = activeDocTypes?.find(
																			(d) => d.id === docType.id
																		);
																		return (
																			<Grid item xs={12}>
																				<CardDocType
																					docType={docType}
																					isChecked={Boolean(docTypeActive)}
																					isCheckedPrivate={Boolean(docTypeActive?.isPrivate)}
																					isCheckedRequired={Boolean(docTypeActive?.isRequired)}
																					key={`${docType.id}-${i}`}
																					onChange={(e) => handleChangeDocType(e, docType)}
																					onChangePrivate={(e) => {
																						handleChangeSwitch(e, docType, 'isPrivate');
																					}}
																					onChangeRequired={(e) => {
																						handleChangeSwitch(e, docType, 'isRequired');
																					}}
																					onSuccessDelete={() => {
																						refetchDocumentTypes();
																					}}
																					onSuccessEdit={() => {
																						refetchDocumentTypes();
																					}}
																				/>
																			</Grid>
																		);
																	})
																) : (
																	<Grid item xs={12}>
																		<Alert severity="info">
																			{values.docTypeView === 'SELECTED'
																				? t('empty.selected.content')
																				: t('empty.default.content')}
																		</Alert>
																	</Grid>
																)}
															</Grid>
														</Box>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									) : (
										<Empty
											content={{ children: t('empty.documentTypes.content') }}
											isLoading={isLoadingDocumentTypes}
											IconCircleProps={{
												color: 'grey',
												type: 'document',
											}}
											sx={(theme) => {
												return {
													padding: theme.spacing(2),
													pointerEvents: 'none',
													position: {
														lg: 'absolute',
													},
													width: '100%',
													height: '100%',
												};
											}}
											title={{ children: t('empty.documentTypes.title') }}
										/>
									)}
								</Paper>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Container>
			<DialogUpdateExpenseTypes
				expenseTypes={expenseTypes}
				isOpen={paramDialog === 'updateExpenseTypes'}
				onClose={() => {
					searchParams.delete('dialog');
					setSearchParams(searchParams);
				}}
			/>
		</>
	);
};
